import { useMemo, useState } from 'react';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import {
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  ResponsiveContainer,
  ComposedChart,
  TooltipContentProps,
} from 'recharts';
import type {
  NameType,
  Payload,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';
import { Button } from '@/components/ui/button.tsx';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';
import { useTranslation } from 'react-i18next';
import {
  useGetChartTradingQuery,
  TimeSeriesPoint,
  JobStatusInfo,
} from '../hooks/queries/use-get-chart-trading';
import { format, subHours } from 'date-fns';

const getStatusBadgeColor = (status: JobStatusInfo): string => {
  switch (status) {
    case 'ready':
      return 'bg-green-100 text-green-800';
    case 'processing':
      return 'bg-blue-100 text-blue-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'partial':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getStatusLabel = (status: JobStatusInfo): string => {
  switch (status) {
    case 'ready':
      return 'ENTSO-E Ready';
    case 'processing':
      return 'ENTSO-E Processing';
    case 'failed':
      return 'ENTSO-E Failed';
    case 'partial':
      return 'ENTSO-E Partial';
    default:
      return 'ENTSO-E Unknown';
  }
};

type HistoricalPoint = {
  time: string;
  typical?: number | null;
  current: number | null;
  availableEnergy?: number;
  stateOfCharge?: number;
};

export const EnergyPricesAndBatteryMetrics = ({ selectedDate }: { selectedDate: Date }) => {
  const { t } = useTranslation();
  const { data: queryResult, isLoading } = useGetChartTradingQuery({
    date: selectedDate,
    type: 'price-delta-heatmap',
  });

  const chartData = queryResult?.data;
  const status = queryResult?.status;

  const isMobile = useMediaQuery('(max-width:640px)');
  const METRIC_COLORS = {
    TYPICAL_LINE: '#a1a1aa',
    CURRENT_LINE: '#3b82f6',
    AVAILABLE_ENERGY: '#22c55e',
    STATE_OF_CHARGE: '#f59e0b',
  } as const;

  const METRIC_KEYS = {
    TYPICAL_LINE: 'typical',
    CURRENT_LINE: 'current',
    AVAILABLE_ENERGY: 'availableEnergy',
    STATE_OF_CHARGE: 'stateOfCharge',
  } as const;

  const CHART_METRICS = [
    {
      id: METRIC_KEYS.TYPICAL_LINE,
      label: 'Typical Pattern',
      color: METRIC_COLORS.TYPICAL_LINE,
      selected: true,
    },
    {
      id: METRIC_KEYS.CURRENT_LINE,
      label: 'Current Prices',
      color: METRIC_COLORS.CURRENT_LINE,
      selected: true,
    },
    {
      id: METRIC_KEYS.AVAILABLE_ENERGY,
      label: 'Available Energy',
      color: METRIC_COLORS.AVAILABLE_ENERGY,
      selected: true,
    },
    {
      id: METRIC_KEYS.STATE_OF_CHARGE,
      label: 'State of Charge',
      color: METRIC_COLORS.STATE_OF_CHARGE,
      selected: true,
    },
  ] as const;

  const [selectedState, setSelectedState] = useState<Record<string, boolean>>({});
  const fields = useMemo(
    () =>
      CHART_METRICS.map((metric) => ({
        ...metric,
        selected: selectedState[metric.id] ?? true,
      })),
    [selectedState]
  );
  const data: HistoricalPoint[] = useMemo(() => {
    if (!chartData) return [];

    const energyPricesData = Array.isArray(chartData.energyPrices)
      ? chartData.energyPrices
      : [];
    const batteryAvailableEnergyData = Array.isArray(chartData.batteryAvailableEnergy)
      ? chartData.batteryAvailableEnergy
      : [];

    const timeMap = new Map<string, HistoricalPoint>();

    energyPricesData.forEach((point: TimeSeriesPoint) => {
      const date = new Date(point.time);
      const adjustedDate = subHours(date, 1);
      const timeKey = format(adjustedDate, 'HH:mm');
      timeMap.set(timeKey, {
        time: timeKey,
        typical: null,
        current: point.value ?? null,
      });
    });

    batteryAvailableEnergyData.forEach((point) => {
      const date = new Date(point.time);
      const timeKey = format(date, 'HH:mm');
      const existing = timeMap.get(timeKey);
      if (existing) {
        existing.availableEnergy = point.availableEnergy;
        existing.stateOfCharge = point.stateOfCharge;
      } else {
        timeMap.set(timeKey, {
          time: timeKey,
          typical: null,
          current: null,
          availableEnergy: point.availableEnergy,
          stateOfCharge: point.stateOfCharge,
        });
      }
    });

    return Array.from(timeMap.values()).sort((a, b) => a.time.localeCompare(b.time));
  }, [chartData]);

  const hasNoData =
    !chartData ||
    ((!chartData.energyPrices || chartData.energyPrices.length === 0) &&
      (!chartData.batteryAvailableEnergy || chartData.batteryAvailableEnergy.length === 0));

  return (
    <DashboardCard isLoading={isLoading} className="min-h-[460px]">
      <div className="mb-4 flex items-center justify-between text-base font-semibold">
        <span>Energy Prices & Battery Metrics</span>
        {status && (
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusBadgeColor(status)}`}
          >
            {getStatusLabel(status)}
          </span>
        )}
      </div>
      {hasNoData ? (
        <div className="flex h-full min-h-[238px] w-full items-center justify-center rounded-md bg-gray-50">
          <span className="text-muted-foreground">
            {t('devices.batteryModule.noDataFound')}
          </span>
        </div>
      ) : (
        <>
          <div className={isMobile ? 'h-[260px] w-full' : 'h-[320px] w-full'}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data}
                margin={{
                  top: 8,
                  right: isMobile ? 0 : 8,
                  bottom: 8,
                  left: isMobile ? -10 : 0,
                }}
              >
                <CartesianGrid vertical={false} stroke="#e5e7eb" />
                <XAxis
                  dataKey="time"
                  minTickGap={40}
                  interval={11}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: isMobile ? 12 : 16 }}
                />
                <YAxis tick={{ fontSize: isMobile ? 12 : 16 }} />
                <Tooltip
                  cursor={false}
                  content={(props) => <ChartTooltipComponent {...props} />}
                />

                {fields.find((f) => f.id === METRIC_KEYS.TYPICAL_LINE)?.selected && (
                  <Line
                    type="monotone"
                    dataKey={METRIC_KEYS.TYPICAL_LINE}
                    stroke={METRIC_COLORS.TYPICAL_LINE}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    name="Typical Pattern"
                  />
                )}
                {fields.find((f) => f.id === METRIC_KEYS.CURRENT_LINE)?.selected && (
                  <Line
                    type="monotone"
                    dataKey={METRIC_KEYS.CURRENT_LINE}
                    stroke={METRIC_COLORS.CURRENT_LINE}
                    strokeWidth={2}
                    dot={false}
                    name="Current Prices"
                  />
                )}
                {fields.find((f) => f.id === METRIC_KEYS.AVAILABLE_ENERGY)?.selected && (
                  <Line
                    type="monotone"
                    dataKey={METRIC_KEYS.AVAILABLE_ENERGY}
                    stroke={METRIC_COLORS.AVAILABLE_ENERGY}
                    strokeWidth={2}
                    dot={false}
                    name="Available Energy"
                  />
                )}
                {fields.find((f) => f.id === METRIC_KEYS.STATE_OF_CHARGE)?.selected && (
                  <Line
                    type="monotone"
                    dataKey={METRIC_KEYS.STATE_OF_CHARGE}
                    stroke={METRIC_COLORS.STATE_OF_CHARGE}
                    strokeWidth={2}
                    dot={false}
                    name="State of Charge"
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="mx-auto mt-2 flex flex-wrap gap-1 md:gap-1">
            {fields.map((f) => (
              <Button
                key={f.id}
                variant="ghost"
                style={{ opacity: !f.selected ? '0.4' : '1' }}
                className="h-min w-min justify-start p-1 px-2"
                onClick={() =>
                  setSelectedState((current) => ({
                    ...current,
                    [f.id]: !f.selected,
                  }))
                }
              >
                <div className="flex items-center gap-2">
                  <span className="size-4 rounded-full" style={{ backgroundColor: f.color }} />
                  <span className="text-start text-sm font-medium text-nowrap text-[#7D7D85]">
                    {f.label}
                  </span>
                </div>
              </Button>
            ))}
          </div>
        </>
      )}
    </DashboardCard>
  );
};

const ChartTooltipComponent = ({
  payload,
  label,
}: TooltipContentProps<ValueType, NameType>) => {
  const formatValue = (p: Payload<ValueType, NameType>): string => {
    if (p?.value === null || p?.value === undefined) return 'N/A';

    const dataKey = p.dataKey as string;
    const value = Number(p.value);

    // State of Charge - display as percentage
    if (dataKey === 'stateOfCharge') {
      return `${value} %`;
    }

    // Available Energy - convert from kWh to MWh
    if (dataKey === 'availableEnergy') {
      if (value < 1000) {
        return `${value.toFixed(2)} kWh`;
      }
      const megaWattValue = (value / 1000).toFixed(2);
      return `${megaWattValue} MWh`;
    }

    // Price values (typical and current) - display as EUR/MWh
    if (dataKey === 'typical' || dataKey === 'current') {
      return `${value.toFixed(2)} EUR/MWh`;
    }

    return String(value);
  };

  return (
    <div className="flex flex-col rounded-md border border-gray-300 bg-white p-4 text-base">
      {label && <p className="mb-2 font-semibold">{label}</p>}
      <div className="mt-2">
        {payload?.map((p: Payload<ValueType, NameType>) => (
          <p style={{ color: p.stroke }} key={p.name}>
            {p.name}: {formatValue(p)}
          </p>
        ))}
      </div>
    </div>
  );
};

export default EnergyPricesAndBatteryMetrics;
